// ─────────────────────────────────────────────────────────────────────────────
// Five Lights Reno — design tokens
// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for the visual language. Read by design/theme.js (MUI),
// design/globalStyles.js (CSS custom properties) and tailwind.config.js.
//
// Plain CommonJS — tailwind.config.js `require()`s this outside webpack/babel.
//
// DESIGN LANGUAGE — "architectural editorial"
//
// The palette is warm paper + warm near-black ink, with a single clay accent.
// That warmth is deliberate: every real project photo we have is a cool-white
// interior (marble-look porcelain, white trim, grey plank floors), so a warm
// ground makes the photography read as inset plates on paper rather than
// blending into the page. Ink sections are used sparingly — hero, featured
// project, closing CTA, footer — so each one lands.
//
// Two families only: Instrument Serif carries every display line, Manrope
// carries body copy, labels and UI. No third family, no icon-font headings.
//
// PHOTOGRAPHY CONSTRAINT (drives most layout decisions):
// The client's photos are phone-shot, 1:1 or 4:5, taken on live job sites.
// There is no wide cinematic architectural photography and inventing some
// would mean using stock. So the layout system frames images as PORTRAIT and
// SQUARE plates, never 16:9 letterbox crops, and captions state plainly what
// each photo actually shows.

const color = {
  // Paper — the page ground and its two quieter steps.
  paper: "#F6F3ED",
  paperDeep: "#EDE8DF",
  paperEdge: "#E2DBD0",

  // Ink — warm near-black. Full-bleed dark sections and all display type.
  ink: "#17150F",
  inkRaised: "#201D16",
  inkSoft: "#2A2621",

  // Text on paper. `graphite` 6.7:1, `mute` 4.9:1 — both clear AA at body size.
  graphite: "#57514A",
  mute: "#6F6960",

  // Text on ink. `onInkMute` 7.3:1.
  onInk: "#F6F3ED",
  onInkMute: "#A8A199",

  // Accent — clay/tobacco, picked out of the stained-wood and brick tones in
  // the real project photos. 4.9:1 on paper. `clayLight` is the on-ink variant
  // (5.6:1) because base clay only reaches 3.4:1 against ink.
  clay: "#A0562F",
  clayDeep: "#7E4224",
  clayLight: "#C87A4C",
  clayTint: "#EFE3DA",

  // Hairlines. This design separates sections with rules and space, never with
  // drop shadows or card borders.
  line: "rgba(23, 21, 15, 0.13)",
  lineSoft: "rgba(23, 21, 15, 0.07)",
  lineOnInk: "rgba(246, 243, 237, 0.16)",
  lineOnInkSoft: "rgba(246, 243, 237, 0.08)",

  white: "#FFFFFF",
  danger: "#9B2C2C",
  success: "#2F6B4F",
};

const font = {
  display: '"Instrument Serif", "Iowan Old Style", Georgia, serif',
  body: '"Manrope", "Segoe UI", system-ui, -apple-system, sans-serif',
};

// Fluid type scale. Display sizes run tight (0.95–1.08) because Instrument
// Serif has a large x-height and looks slack at normal leading.
const type = {
  displayXl: {
    fontFamily: font.display,
    fontWeight: 400,
    fontSize: "clamp(3.1rem, 8.6vw, 7.25rem)",
    lineHeight: 0.95,
    letterSpacing: "-0.02em",
  },
  displayLg: {
    fontFamily: font.display,
    fontWeight: 400,
    fontSize: "clamp(2.5rem, 5.6vw, 4.5rem)",
    lineHeight: 1.0,
    letterSpacing: "-0.015em",
  },
  displayMd: {
    fontFamily: font.display,
    fontWeight: 400,
    fontSize: "clamp(2rem, 3.9vw, 3.1rem)",
    lineHeight: 1.06,
    letterSpacing: "-0.01em",
  },
  displaySm: {
    fontFamily: font.display,
    fontWeight: 400,
    fontSize: "clamp(1.55rem, 2.6vw, 2.15rem)",
    lineHeight: 1.12,
    letterSpacing: "-0.005em",
  },
  // Small-caps label. Always paired with a hairline rule — see ui/Label.jsx.
  label: {
    fontFamily: font.body,
    fontWeight: 700,
    fontSize: "0.6875rem",
    lineHeight: 1,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  lead: {
    fontFamily: font.body,
    fontWeight: 400,
    fontSize: "clamp(1.0625rem, 1.25vw, 1.3125rem)",
    lineHeight: 1.62,
  },
  body: {
    fontFamily: font.body,
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: 1.72,
  },
  bodySm: {
    fontFamily: font.body,
    fontWeight: 400,
    fontSize: "0.9375rem",
    lineHeight: 1.66,
  },
  // Photo captions and meta rows.
  caption: {
    fontFamily: font.body,
    fontWeight: 500,
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    letterSpacing: "0.01em",
  },
  // Oversized index numerals on service rows / process steps.
  numeral: {
    fontFamily: font.body,
    fontWeight: 500,
    fontSize: "0.75rem",
    letterSpacing: "0.12em",
    fontVariantNumeric: "tabular-nums",
  },
};

// Vertical rhythm between full sections, and the page gutter.
const space = {
  sectionY: "clamp(4.5rem, 10vw, 10.5rem)",
  sectionYTight: "clamp(3.25rem, 6.5vw, 6rem)",
  gutter: "clamp(1.25rem, 5vw, 4.5rem)",
  gridGap: "clamp(1.25rem, 2.4vw, 2.5rem)",
  maxWidth: 1440,
  // Narrow measure for long-form editorial copy (~68 characters).
  measure: 640,
};

// Images are framed at these ratios only — they match the source photography.
const ratio = {
  portrait: "4 / 5",
  tall: "3 / 4.4",
  square: "1 / 1",
  landscape: "5 / 4",
};

const motion = {
  // Long, unhurried easing. A renovation contractor should read as steady.
  ease: "cubic-bezier(0.22, 0.61, 0.36, 1)",
  easeOut: "cubic-bezier(0.16, 1, 0.3, 1)",
  fast: "180ms",
  base: "320ms",
  slow: "620ms",
  reveal: "760ms",
};

const radius = {
  // Near-square corners throughout — architectural, not app-like.
  none: 0,
  sm: "2px",
  pill: "999px",
};

const zIndex = {
  header: 1200,
  drawer: 1300,
  dialog: 1400,
};

module.exports = { color, font, type, space, ratio, motion, radius, zIndex };
